import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'sonner';

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const sessionTerminatedNotified = useRef(false);

  // ---- Global single-active-session 401 interceptor ----
  // We wrap window.fetch ONCE so every API call (existing + future) auto
  // signs the user out when the backend says `session_terminated` (i.e.
  // their account just signed in on another device/browser).
  useEffect(() => {
    if (window.__cognitiaindia_fetch_patched) return;
    window.__cognitiaindia_fetch_patched = true;
    const origFetch = window.fetch.bind(window);
    window.fetch = async (...args) => {
      const res = await origFetch(...args);
      // Run the interceptor side-effect WITHOUT awaiting body parse — body
      // streams sometimes hang behind preview-proxy buffering. We have all
      // the info we need from status + URL + localStorage.
      try {
        if (res.status === 401) {
          const url = (args[0] && (args[0].url || args[0])) || '';
          const isAuthEndpoint = typeof url === 'string' && (
            url.includes('/auth/login') || url.includes('/auth/session') || url.includes('/auth/register')
          );
          const tokenInStore = localStorage.getItem('token');
          if (!isAuthEndpoint && tokenInStore) {
            // Any 401 on an authed API call while we hold a token == our
            // session is no longer valid (logged in elsewhere, expired,
            // server-revoked, etc.) — kick out cleanly.
            if (!sessionTerminatedNotified.current) {
              sessionTerminatedNotified.current = true;
              toast.error('You signed in on another device — this session has ended.', { duration: 6000 });
            }
            localStorage.removeItem('token');
            setTimeout(() => { window.location.href = '/login'; }, 600);
          }
        }
      } catch { /* swallow interceptor errors so the original caller still gets res */ }
      return res;
    };
  }, []);

  const checkAuth = useCallback(async () => {
    try {
      const storedToken = localStorage.getItem('token');
      if (!storedToken) {
        setLoading(false);
        return;
      }

      const response = await fetch(`${API_URL}/auth/me`, {
        headers: {
          'Authorization': `Bearer ${storedToken}`
        },
        credentials: 'include'
      });

      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
        setToken(storedToken);
      } else {
        localStorage.removeItem('token');
        setToken(null);
        setUser(null);
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      localStorage.removeItem('token');
      setToken(null);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // ---- Session heartbeat: detects "you signed in on another device" within
  // ---- ~15s even if the user is just clicking around the SPA without
  // ---- triggering an API call. Combines:
  //   1. Periodic heartbeat every 15s while a token is in localStorage.
  //   2. Immediate re-check whenever the tab regains focus / becomes visible.
  // The actual logout is handled by the global fetch interceptor above
  // (it sees the 401 `session_terminated` from /auth/me and kicks the user
  // out with a toast).
  useEffect(() => {
    if (!token) return;
    const ping = async () => {
      try {
        await fetch(`${API_URL}/auth/me`, {
          headers: { 'Authorization': `Bearer ${token}` },
          credentials: 'include',
          // The interceptor will handle a 401 globally; we don't need the response.
        });
      } catch { /* offline — ignore */ }
    };
    const id = setInterval(ping, 15000);  // 15s heartbeat
    const onFocus = () => ping();
    const onVisibility = () => { if (document.visibilityState === 'visible') ping(); };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      clearInterval(id);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [token]);

  const login = async (email, password, _forceLogin = false) => {
    // Single-active-session: backend always terminates prior sessions on login,
    // no confirmation needed. (`_forceLogin` kept for backward compat.)
    const response = await fetch(`${API_URL}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password, force_login: true }),
      credentials: 'include'
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.detail || 'Login failed');
    }

    localStorage.setItem('token', data.token);
    setToken(data.token);
    setUser(data.user);
    return data;
  };

  const register = async (userData) => {
    const response = await fetch(`${API_URL}/auth/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(userData),
      credentials: 'include'
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Registration failed');
    }

    const data = await response.json();
    localStorage.setItem('token', data.token);
    setToken(data.token);
    setUser(data.user);
    return data;
  };

  const loginWithGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleGoogleCallback = async (sessionId) => {
    const response = await fetch(`${API_URL}/auth/session`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ session_id: sessionId }),
      credentials: 'include'
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Google auth failed');
    }

    const data = await response.json();
    localStorage.setItem('token', data.session_token);
    setToken(data.session_token);
    setUser(data.user);
    return data;
  };

  const logout = async () => {
    try {
      await fetch(`${API_URL}/auth/logout`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        credentials: 'include'
      });
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('token');
      setToken(null);
      setUser(null);
    }
  };

  const updateUser = (updatedUser) => {
    setUser(updatedUser);
  };

  return (
    <AuthContext.Provider value={{
      user,
      token,
      loading,
      login,
      register,
      loginWithGoogle,
      handleGoogleCallback,
      logout,
      updateUser,
      checkAuth,
      isAuthenticated: !!user
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
